/**
 * Shared visual language for generated business documents.
 *
 * These values mirror the Client Invoice renderer so quotations, proposals,
 * assignment confirmations, and their supplier copies feel like one document
 * family. Keep renderer-specific layout and pagination outside this object.
 */
export const CLIENT_DOCUMENT_FONT_FAMILY = 'Inter, Helvetica, Arial, sans-serif';
export const CLIENT_DOCUMENT_BODY_COLOR = '#404040';
// Single print accent shared by every generated document in the NOC family.
export const CLIENT_DOCUMENT_BLUE = '#1F4E79';
export const CLIENT_DOCUMENT_MUTED_BLUE = '#1F4E79';
export const CLIENT_DOCUMENT_LABEL_COLOR = '#414552';
export const CLIENT_DOCUMENT_BORDER = '#d9d9d9';
export const CLIENT_DOCUMENT_TABLE_HEADER = '#f4f4f4';

export const CLIENT_DOCUMENT_THEME = Object.freeze({
	fontFamily: CLIENT_DOCUMENT_FONT_FAMILY,
	pageBackground: '#fff',
	canvasBackground: '#f5f5f5',
	textColor: '#0C0E16',
	secondaryTextColor: '#4B5563',
	mutedTextColor: '#6b7280',
	ruleColor: '#E0E0E0',
	strongRuleColor: '#0C0E16',
	tableGroupBackground: '#f8f9fa',
	tableRangeBackground: '#f1f3f5',
	titleFontSize: '26px',
	titleFontWeight: 700,
	bodyFontSize: '14px',
	bodyLineHeight: 1.5,
	sectionTitleFontSize: '16px',
	sectionTitleFontWeight: 700,
	tableHeaderFontSize: '12px',
	tableHeaderFontWeight: 500,
	tableGroupFontSize: '12px',
	tableGroupFontWeight: 600,
	footerFontSize: '12px',
});

const CLIENT_DOCUMENT_FONT_LOAD_REQUESTS = Object.freeze([
	'400 13px Inter',
	'italic 400 13px Inter',
	'500 13px Inter',
	'600 13px Inter',
	'700 13px Inter',
]);

const CLIENT_DOCUMENT_FONT_SAMPLE = 'New Order Clean £ ĄąĆć ЖжЇї';

/** Ensure pagination and canvas-based PDF export measure the bundled font, not a fallback. */
export const waitForClientDocumentFonts = async (ownerDocument = null) => {
	const resolvedDocument =
		ownerDocument || (typeof document !== 'undefined' ? document : null);
	const fonts = resolvedDocument?.fonts;
	if (!fonts || typeof fonts.load !== 'function') return;

	await Promise.all(
		CLIENT_DOCUMENT_FONT_LOAD_REQUESTS.map((font) =>
			fonts.load(font, CLIENT_DOCUMENT_FONT_SAMPLE),
		),
	);
	if (fonts.ready) await fonts.ready;
};

// A copy that cannot get its fonts is drawn anyway after this long, rather than not at all.
const CLONED_FONTS_TIMEOUT_MS = 5000;

/**
 * For html2canvas's onclone option. html2canvas draws a hidden copy of the page, and the copy
 * loads its fonts afresh. Measured before Inter arrives there (Safari can report the fonts ready
 * too early), words are placed by the narrower fallback font but drawn in Inter, which is 7% wider,
 * so they run into each other. This holds the drawing until the copy has its fonts.
 */
export const waitForClonedDocumentFonts = async (clonedDocument) => {
	// Laying the copy out starts the loads of the fonts it uses, so fonts.ready waits for them too.
	void clonedDocument?.body?.offsetHeight;
	let timer = null;
	await Promise.race([
		waitForClientDocumentFonts(clonedDocument).catch(() => {}),
		new Promise((resolve) => {
			timer = setTimeout(resolve, CLONED_FONTS_TIMEOUT_MS);
		}),
	]);
	clearTimeout(timer);
};

/**
 * Restrained type scale for the denser, cell-based document templates.
 *
 * Client Invoice keeps the roomier scale above. Assignment confirmations,
 * quotations, and proposals need a quieter title and more compact body copy
 * because their information hierarchy is deeper and their tables are denser.
 */
export const CLIENT_STRUCTURED_DOCUMENT_THEME = Object.freeze({
	...CLIENT_DOCUMENT_THEME,
	accentColor: CLIENT_DOCUMENT_BLUE,
	titleFontSize: '22px',
	titleLineHeight: 1.15,
	titleLetterSpacing: '-0.01em',
	bodyFontSize: '13px',
	bodyLineHeight: 1.5,
	sectionTitleFontSize: '15px',
	sectionTitleLineHeight: 1.3,
	subsectionTitleFontSize: '13px',
	subsectionTitleLineHeight: 1.35,
	tableHeaderFontSize: '11.5px',
	tableHeaderFontWeight: 600,
	tableGroupFontSize: '11.5px',
	tableCellLineHeight: 1.4,
	footerFontSize: '10.5px',
	footerLineHeight: 1.35,
});

export const DEFAULT_CLIENT_DOCUMENT_COMPANY = {
	name: 'New Order Clean Ltd',
	address: "Office 2, Bennet's House, 21 Leyton Road, Harpenden, AL5 2HU",
	companyNumber: '13205534',
	phone: '07553 702075',
	email: 'info@neworderclean.co.uk',
	website: 'www.neworderclean.co.uk',
};

export const clone = (value) => JSON.parse(JSON.stringify(value));

export const normalizeText = (value, fallback = '') =>
	value === null || value === undefined ? fallback : String(value);

export const normalizeStringArray = (value, fallback = []) =>
	(Array.isArray(value) ? value : fallback).map((item) => normalizeText(item));

/**
 * Schedule rows can be set in bold, in italics and in the documents' blue. Only the formats that
 * are on are kept, so a plain row stays { label, value }.
 */
export const normalizeRowFormat = (value = {}) => ({
	...(value?.bold === true ? { bold: true } : {}),
	...(value?.italic === true ? { italic: true } : {}),
	...(value?.color === 'blue' ? { color: 'blue' } : {}),
});

/** Class names of a formatted row in a generated document, such as `schedule-row--bold`. */
export const rowFormatClassNames = (row = {}, block) =>
	[
		row.bold && `${block}--bold`,
		row.italic && `${block}--italic`,
		row.color === 'blue' && `${block}--blue`,
	].filter(Boolean);

export const escapeHtml = (value) => {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
};

/* Settings values (e.g. address) can be multi-line; the footer needs one flowing line. */
const flattenFooterValue = (value) =>
	String(value || '')
		.split(/\r?\n/)
		.map((part) => part.trim().replace(/,+$/, ''))
		.filter(Boolean)
		.join(', ');

/* Phone settings often already carry a "Mob.:" label; avoid "Mob. Mob.: ...". */
const normalizeFooterPhone = (value) =>
	flattenFooterValue(value).replace(/^mob(?:ile)?\.?\s*:?\s*/i, '');

export const buildClientDocumentFooterText = (company = {}) =>
	[
		flattenFooterValue(company.name),
		flattenFooterValue(company.address),
		company.companyNumber ? `Company No. ${flattenFooterValue(company.companyNumber)}` : '',
		company.phone ? `Mob. ${normalizeFooterPhone(company.phone)}` : '',
		flattenFooterValue(company.email),
		flattenFooterValue(company.website),
	]
		.filter(Boolean)
		.join('  ');

/*
 * Footer text produced by the previous builder (raw values, no flattening).
 * Needed to recognize auto-generated footers in saved documents so they can
 * be refreshed instead of being treated as manual edits.
 */
export const buildLegacyClientDocumentFooterText = (company = {}) =>
	[
		company.name,
		company.address,
		company.companyNumber ? `Company No. ${company.companyNumber}` : '',
		company.phone ? `Mob. ${company.phone}` : '',
		company.email,
		company.website,
	]
		.filter(Boolean)
		.join('  ');

export const normalizeClientDocumentCompany = (value = {}) => ({
	name: normalizeText(value.name, DEFAULT_CLIENT_DOCUMENT_COMPANY.name),
	address: normalizeText(value.address, DEFAULT_CLIENT_DOCUMENT_COMPANY.address),
	companyNumber: normalizeText(value.companyNumber, DEFAULT_CLIENT_DOCUMENT_COMPANY.companyNumber),
	phone: normalizeText(value.phone, DEFAULT_CLIENT_DOCUMENT_COMPANY.phone),
	email: normalizeText(value.email, DEFAULT_CLIENT_DOCUMENT_COMPANY.email),
	website: normalizeText(value.website, DEFAULT_CLIENT_DOCUMENT_COMPANY.website),
});

export const renderClientDocumentFooterText = (value) =>
	escapeHtml(value).replace(/\r?\n/g, '<br>');

/**
 * The document's name at the top of its later pages, as on the director's Word documents:
 * "NOC-022/EP – Emergency Procedures".
 */
export const buildClientDocumentPageHeaderText = (reference, title) =>
	[reference, title]
		.map((value) => normalizeText(value).replace(/\s+/g, ' ').trim())
		.filter(Boolean)
		.join(' – ');

export const createSafePdfFilename = (reference, fallback = 'document') => {
	const safeReference = String(reference || fallback)
		.replace(/[^a-zA-Z0-9._-]+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 80);
	return `${safeReference || fallback}.pdf`;
};
